import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import io from 'socket.io-client';
import { getToken } from '../api/http';

const SocketContext = createContext();

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

// Connection state enum for better tracking
const ConnectionState = {
  DISCONNECTED: 'disconnected',
  CONNECTING: 'connecting',
  CONNECTED: 'connected',
  RECONNECTING: 'reconnecting'
};

export const SocketProvider = ({ children }) => {
  const [socket, setSocket] = useState(null);
  const [connectionState, setConnectionState] = useState(ConnectionState.DISCONNECTED);
  const [offlineAlerts, setOfflineAlerts] = useState([]);
  const [mqttCircuitState, setMqttCircuitState] = useState(null);
  
  // Track reconnection attempts
  const reconnectAttempts = useRef(0);
  const maxReconnectDelay = 30000; // Max 30 seconds between attempts
  const baseReconnectDelay = 1000; // Start with 1 second

  // Calculate exponential backoff delay
  const getReconnectDelay = useCallback(() => {
    const delay = Math.min(
      baseReconnectDelay * Math.pow(2, reconnectAttempts.current),
      maxReconnectDelay
    );
    // Add jitter (0-25% of delay)
    return delay + Math.random() * delay * 0.25;
  }, []);

  // Derived state for backwards compatibility
  const isConnected = connectionState === ConnectionState.CONNECTED;

  useEffect(() => {
    let newSocket = null;
    let reconnectTimer = null;
    
    const connectSocket = () => {
      // Clean up existing socket if any
      if (newSocket) {
        newSocket.removeAllListeners();
        newSocket.close();
      }

      setConnectionState(ConnectionState.CONNECTING);

      // Create new socket connection - use relative URL for Docker compatibility
      const socketUrl = window.location.origin;
      newSocket = io(socketUrl, {
        transports: ['websocket', 'polling'], // Allow fallback to polling
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 30000,
        reconnectionAttempts: Infinity, // Never give up
        timeout: 20000,
        withCredentials: true,
        // Staff token for the server-side socket auth middleware.
        auth: { token: getToken() },
        // Improved connection options
        forceNew: false,
        multiplex: true
      });

      // Set up event handlers
      newSocket.on('connect', () => {
        console.log('[Socket] Connected to server');
        setConnectionState(ConnectionState.CONNECTED);
        reconnectAttempts.current = 0; // Reset on successful connection
      });

      newSocket.on('disconnect', (reason) => {
        console.log('[Socket] Disconnected from server:', reason);
        setConnectionState(ConnectionState.DISCONNECTED);
        
        // If the server disconnected us, try to reconnect manually
        if (reason === 'io server disconnect') {
          // Server initiated disconnect, need to manually reconnect
          scheduleReconnect();
        }
        // For other reasons, socket.io will handle reconnection automatically
      });

      newSocket.on('connect_error', (error) => {
        console.error('[Socket] Connection error:', error.message);
        setConnectionState(ConnectionState.DISCONNECTED);
      });

      newSocket.on('reconnect_attempt', (attemptNumber) => {
        console.log(`[Socket] Reconnection attempt #${attemptNumber}`);
        setConnectionState(ConnectionState.RECONNECTING);
        reconnectAttempts.current = attemptNumber;
      });

      newSocket.on('reconnect', (attemptNumber) => {
        console.log(`[Socket] Reconnected after ${attemptNumber} attempts`);
        setConnectionState(ConnectionState.CONNECTED);
        reconnectAttempts.current = 0;
      });

      newSocket.on('reconnect_error', (error) => {
        console.error('[Socket] Reconnection error:', error.message);
      });

      newSocket.on('reconnect_failed', () => {
        console.log('[Socket] Built-in reconnection exhausted, using custom backoff');
        // Socket.io's built-in reconnection failed, use our own with longer backoff
        scheduleReconnect();
      });

      // Handle offline locker alerts
      newSocket.on('lockers-offline', (data) => {
        console.log('[Socket] Lockers went offline:', data);
        const newAlert = {
          id: Date.now(),
          type: 'offline',
          message: `${data.count} locker(s) went offline: ${data.lockers.join(', ')}`,
          timestamp: new Date()
        };
        setOfflineAlerts(prev => [...prev, newAlert]);
        
        // Auto-remove alert after 10 seconds
        setTimeout(() => {
          setOfflineAlerts(prev => prev.filter(alert => alert.id !== newAlert.id));
        }, 10000);
      });

      // Handle MQTT circuit breaker state changes from backend
      newSocket.on('mqtt-circuit-state', (data) => {
        console.log('[Socket] MQTT circuit breaker state:', data);
        setMqttCircuitState(data);
      });

      // Handle server health degradation
      newSocket.on('server-health', (data) => {
        if (data.status === 'degraded' || data.status === 'unhealthy') {
          console.warn('[Socket] Server health degraded:', data);
        }
      });

      setSocket(newSocket);
    };

    const scheduleReconnect = () => {
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
      }
      
      const delay = getReconnectDelay();
      console.log(`[Socket] Scheduling reconnect in ${Math.round(delay / 1000)}s`);
      
      reconnectTimer = setTimeout(() => {
        reconnectAttempts.current++;
        console.log(`[Socket] Custom reconnection attempt #${reconnectAttempts.current}`);
        connectSocket();
      }, delay);
    };

    // Initial connection
    connectSocket();

    // Handle page visibility changes - reconnect when page becomes visible
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !isConnected) {
        console.log('[Socket] Page became visible, checking connection...');
        if (newSocket && !newSocket.connected) {
          newSocket.connect();
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Handle online/offline events
    const handleOnline = () => {
      console.log('[Socket] Browser came online, reconnecting...');
      if (newSocket && !newSocket.connected) {
        newSocket.connect();
      }
    };
    window.addEventListener('online', handleOnline);

    // Cleanup function
    return () => {
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
      }
      if (newSocket) {
        console.log('[Socket] Cleaning up connection...');
        newSocket.removeAllListeners();
        newSocket.close();
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('online', handleOnline);
    };
  }, [getReconnectDelay]);

  return (
    <SocketContext.Provider value={{ 
      socket, 
      isConnected, 
      connectionState,
      offlineAlerts,
      mqttCircuitState,
      ConnectionState // Export enum for consumers
    }}>
      {children}
    </SocketContext.Provider>
  );
};

export { SocketContext };
